import { GridApi } from 'ag-grid-community';
import { AgGridColumn } from 'ag-grid-react';
import { Dictionary, isEqual } from 'lodash';
import moment from 'moment';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { ProjectId, TaskIdV2, Team, User } from 'api/models';
import { Allocation, ResourceType } from 'api/resource-management/types';
import { Task } from 'api/tasks';
import { Button } from 'components/atoms/button';
import { CustomLayoutDrawerContent as DrawerContent } from 'components/atoms/drawer';
import { Grid } from 'components/atoms/grid';
import { humanise } from 'components/atoms/humanised-date';
import { TableSkeleton } from 'components/atoms/table';
import { useTaskDrawer } from 'components/organisms/task-drawer';
import { WorkingDaysConfiguration } from 'components/organisms/user-capacity';
import { useTranslate } from 'hooks/use-translation';
import { TeamDetail, UserDetail } from '../manage-allocation';
import { ProjectsWithMembers, SchedulerReference } from '../types';
import { ignoreMinutesIfZero } from '../utils/dates';
import { asAllocationPayload } from '../utils/events-conversion/common';
import {
  AllocationDetail,
  AllocationDetailLabel,
  AllocationDetailRow,
  AllocationDetailSecondaryValue,
  AllocationDetailValueWrapper,
  Drawer,
  GridContainer,
  LoadingOverlay,
  UserOrTeamAvatarRow,
} from './component';
import {
  AssigneeRenderer,
  DueDateRenderer,
  EffortRenderer,
  TaskNameRenderer,
} from './table-cells';

interface Props {
  schedulerRef: SchedulerReference;
  userLookup: Dictionary<User>;
  teamLookup: Dictionary<Team>;
  projectLookup: Dictionary<ProjectsWithMembers>;
  allocation?: Allocation;
  sourceTasksGetter: (allocation: Allocation) => Promise<Task[]>;
  onCancel: () => void;
  isHoursPerWeek: boolean;
  workingDaysConfig: WorkingDaysConfiguration;
}

export const AutoAllocateSourceTasksDrawer: FC<Props> = ({
  allocation,
  userLookup,
  teamLookup,
  projectLookup,
  sourceTasksGetter,
  onCancel,
  schedulerRef,
  isHoursPerWeek,
  workingDaysConfig,
}) => {
  const translate = useTranslate();
  const user = useMemo(
    () =>
      allocation != null && allocation?.type === ResourceType.User
        ? userLookup[allocation.userId]
        : undefined,
    [allocation, userLookup],
  );
  const team = useMemo(
    () =>
      allocation != null && allocation?.type === ResourceType.Team
        ? teamLookup[allocation.teamId]
        : undefined,
    [allocation, teamLookup],
  );
  const referenceTeamName = useMemo(
    () =>
      allocation != null &&
      allocation.type === ResourceType.User &&
      allocation.referenceTeamId
        ? projectLookup[allocation.projectId].projectMembers.find(
            ({ user: { userId }, referenceTeam }) =>
              referenceTeam != null &&
              userId === allocation.userId &&
              referenceTeam.teamId === allocation.referenceTeamId,
          )?.referenceTeam?.teamName
        : undefined,
    [allocation, projectLookup],
  );
  const [isSourceTasksFetching, setSourceTasksFetchingStatus] = useState(false);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const onGridReady = useCallback((params) => {
    setGridApi(params.api);
  }, []);

  useEffect(() => {
    if (allocation != null && gridApi != null && !isSourceTasksFetching) {
      setSourceTasksFetchingStatus(true);
      sourceTasksGetter(allocation)
        .then((tasks) => {
          gridApi.setRowData(
            tasks.map((task) => ({
              ...task,
              assignee: {
                users: (task.assignee?.users ?? [])
                  .map(({ userId }) => userLookup[userId])
                  .filter(Boolean),
                teams: (task.assignee?.teams ?? [])
                  .map(({ teamId }) => teamLookup[teamId])
                  .filter(Boolean),
              },
            })),
          );
        })
        .finally(() => {
          setSourceTasksFetchingStatus(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allocation, gridApi, sourceTasksGetter, userLookup, teamLookup]);
  const [lastOpenedAllocation, setLastOpenedAllocation] =
    useState<Allocation>();
  const [openedTask, show] = useTaskDrawer();
  const close = useCallback(() => {
    onCancel();
    setGridApi(null);
  }, [onCancel]);

  useEffect(() => {
    if (lastOpenedAllocation == null) {
      return;
    }
    if (openedTask[0] === 'invalid') {
      const allocationFromScheduler =
        schedulerRef.current.instance.eventStore.getById(
          lastOpenedAllocation.id,
        );

      if (
        allocationFromScheduler == null ||
        !isEqual(
          asAllocationPayload(allocationFromScheduler),
          lastOpenedAllocation,
        )
      ) {
        setLastOpenedAllocation(undefined);
        close();
      }
    }
  }, [close, lastOpenedAllocation, openedTask, schedulerRef]);

  return (
    <Drawer visible={allocation != null} onClose={close} destroyOnClose>
      <DrawerContent>
        <DrawerContent.Title>
          <FormattedMessage id="resource_management_auto_allocation.source_tasks.title" />
        </DrawerContent.Title>
        <DrawerContent.Content>
          <UserOrTeamAvatarRow>
            {user != null ? (
              <UserDetail user={user} referenceTeamName={referenceTeamName} />
            ) : team != null ? (
              <TeamDetail team={team} />
            ) : null}
          </UserOrTeamAvatarRow>
          {allocation != null ? (
            <AllocationDetailRow>
              <AllocationDetail>
                <AllocationDetailLabel>
                  <FormattedMessage
                    id="resource_management.allocations.number_of_working_days.label"
                    values={{ showDates: false }}
                  />
                </AllocationDetailLabel>
                <AllocationDetailValueWrapper>
                  {allocation.totalMinutes / allocation.minutes}
                  <AllocationDetailSecondaryValue>
                    <span>({humanise(moment(allocation.startDate))}</span>
                    <span>-</span>
                    <span>{humanise(moment(allocation.endDate))})</span>
                  </AllocationDetailSecondaryValue>
                </AllocationDetailValueWrapper>
              </AllocationDetail>
              <AllocationDetail>
                <AllocationDetailLabel>
                  <FormattedMessage
                    id={
                      isHoursPerWeek
                        ? 'resource_management.allocations.time_per_week.label'
                        : 'resource_management.auto_allocate.time_per_day.label'
                    }
                  />
                </AllocationDetailLabel>
                <AllocationDetailValueWrapper>
                  {ignoreMinutesIfZero(
                    allocation.minutes *
                      (isHoursPerWeek
                        ? Object.values(workingDaysConfig).filter(Boolean)
                            .length
                        : 1),
                  )}
                </AllocationDetailValueWrapper>
              </AllocationDetail>
              <AllocationDetail>
                <AllocationDetailLabel>
                  <FormattedMessage id="resource_management.allocations.total_allocation_only.label" />
                </AllocationDetailLabel>
                <AllocationDetailValueWrapper>
                  {ignoreMinutesIfZero(allocation.totalMinutes)}
                </AllocationDetailValueWrapper>
              </AllocationDetail>
            </AllocationDetailRow>
          ) : null}
          <GridContainer className="ag-theme-alpine">
            <Grid
              suppressContextMenu
              suppressRowClickSelection
              suppressCellSelection
              suppressLoadingOverlay
              onGridReady={onGridReady}
              frameworkComponents={{
                EffortRenderer,
                AssigneeRenderer,
                DueDateRenderer,
                TaskNameRenderer,
              }}
              defaultColDef={{
                filter: false,
                sortable: false,
                resizable: false,
                suppressMovable: true,
                suppressMenu: true,
              }}
              onRowClicked={({ data }) => {
                show(ProjectId(data.project.projectId), TaskIdV2(data.taskId));
                setLastOpenedAllocation(allocation);
              }}
            >
              <AgGridColumn
                width={224}
                field="taskId"
                cellRenderer="TaskNameRenderer"
                editable={false}
                headerName={translate('task_name')}
              />
              <AgGridColumn
                width={120}
                field="effort"
                cellRenderer="EffortRenderer"
                editable={false}
                headerName={translate('effort')}
              />
              <AgGridColumn
                width={120}
                field="assignee"
                editable={false}
                cellRenderer="AssigneeRenderer"
                headerName={translate('assigned_to')}
              />
              <AgGridColumn
                width={184}
                field="dueDate"
                editable={false}
                cellRenderer="DueDateRenderer"
                headerName={translate('due_date')}
              />
            </Grid>
            {isSourceTasksFetching ? (
              <LoadingOverlay>
                <TableSkeleton rowCount={6} columnCount={4} />
              </LoadingOverlay>
            ) : null}
          </GridContainer>
        </DrawerContent.Content>
        <DrawerContent.Footer>
          <Button type="primary" onClick={close}>
            <FormattedMessage id="done" />
          </Button>
        </DrawerContent.Footer>
      </DrawerContent>
    </Drawer>
  );
};
